'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/utils/supabase/client';
import { uploadFile } from '@/utils/supabase/storage';

interface PhotoSubmissionProps {
  onSubmit: (data: FormData) => void;
}

const PhotoSubmission: React.FC<PhotoSubmissionProps> = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    category: '',
    location: '',
    description: '',
    consent: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categories = [
    'Health Services',
    'Community Support',
    'Events',
    'Volunteer Activities',
    'Success Stories',
    'Other'
  ];

  const locations = ['Sarthana', 'Varachha', 'Kadodara', 'Other'];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const fileInput = document.getElementById('photo') as HTMLInputElement;
      const file = fileInput.files?.[0];
      if (!file) throw new Error("Please select a photo.");

      // Upload image
      const imageUrl = await uploadFile(file, 'images');

      // Insert record
      const supabase = createClient();
      const { error: insertError } = await supabase.from('galleryphoto').insert([{
        title: `${formData.name} - ${formData.category}`,
        image_url: imageUrl,
        category: formData.category,
        location: formData.location,
        description: formData.description,
        date: new Date().toISOString().split('T')[0]
      }]);

      if (insertError) throw insertError;

      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        category: '',
        location: '',
        description: '',
        consent: false
      });
      if (fileInput) fileInput.value = '';

      setTimeout(() => setSubmitSuccess(false), 5000);
      
      const fd = new FormData(e.target as HTMLFormElement);
      onSubmit(fd);
    } catch (err: any) {
      console.error('Error submitting photo:', err);
      
      // Extract better error message
      let errorMessage = 'Failed to submit photo. Please try again.';
      if (err?.message) {
        errorMessage = err.message;
      } else if (err?.error_description) {
        errorMessage = err.error_description;
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else if (err?.details) {
        errorMessage = err.details;
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-lg p-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
            <Icon name="CloudArrowUpIcon" size={20} />
            <span className="text-sm font-semibold">Share Your Story</span>
          </div>
          <h2 className="text-3xl font-heading font-bold text-foreground mb-3">
            Submit Your Photos
          </h2>
          <p className="text-muted-foreground">
            Help us showcase the impact of our community by sharing your photos and experiences
          </p>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive rounded-lg p-4 mb-6 flex items-start space-x-3">
            <Icon name="ExclamationCircleIcon" size={24} className="text-destructive flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-destructive mb-1">Submission Failed</h4>
              <p className="text-sm text-foreground">
                {error}
              </p>
            </div>
          </div>
        )}

        {submitSuccess && (
          <div className="bg-success/10 border border-success rounded-lg p-4 mb-6 flex items-start space-x-3">
            <Icon name="CheckCircleIcon" size={24} className="text-success flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-success mb-1">Submission Successful!</h4>
              <p className="text-sm text-foreground">
                Thank you for sharing your photos. Our team will review and add them to the gallery soon.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-card rounded-lg shadow-subtle p-6 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2.5 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2.5 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                placeholder="your.email@example.com"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2.5 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                placeholder="+91 87587 58778"
              />
            </div>

            <div>
              <label htmlFor="category" className="block text-sm font-medium text-foreground mb-2">
                Category *
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2.5 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
              >
                <option value="">Select category</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="location" className="block text-sm font-medium text-foreground mb-2">
                Location *
              </label>
              <select
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2.5 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
              >
                <option value="">Select location</option>
                {locations.map((loc) => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="photo" className="block text-sm font-medium text-foreground mb-2">
                Upload Photo *
              </label>
              <input
                type="file"
                id="photo"
                name="photo"
                accept="image/*"
                required
                className="w-full px-4 py-2.5 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary file:text-primary-foreground file:cursor-pointer hover:file:bg-primary/90"
              />
            </div>
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-foreground mb-2">
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              rows={4}
              className="w-full px-4 py-2.5 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground resize-none"
              placeholder="Tell us about this photo and its significance..."
            />
          </div>

          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="consent"
              name="consent"
              checked={formData.consent}
              onChange={handleInputChange}
              required
              className="mt-1 w-4 h-4 text-primary border-input rounded focus:ring-2 focus:ring-primary"
            />
            <label htmlFor="consent" className="text-sm text-muted-foreground">
              I consent to Seva Setu Trust using this photo for promotional purposes on their website, social media, and other marketing materials. I confirm that I have the right to share this photo. *
            </label>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg font-heading font-semibold hover:bg-primary/90 transition-all duration-300 shadow-subtle hover:shadow-cta disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {isSubmitting ? (
              <>
                <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <Icon name="CloudArrowUpIcon" size={20} />
                <span>Submit Photo</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PhotoSubmission;